import { useState, useEffect } from 'react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';

function timeAgo(dateStr) {
  if (!dateStr) return null;
  const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

// Per-resource wiring. Everything that differs between a video share link and a
// footage share link lives here, so the panel below is written once.
//
// 'video' is unchanged from before this component was generalised — the three
// existing callers (VideoCard, AiProduction, Qual) pass `videoAssetId` and keep
// working exactly as they did.
const RESOURCES = {
  video: {
    base:        '/api/share-links',
    listPath:    id => `/api/share-links/video/${id}`,
    createBody:  id => ({ videoAssetId: id }),
    openLabel:   '\u2934 Share Video',
    showComments: true,
    // Video share creation is not gated on isExternal upstream; left as-is
    // rather than changed under three existing callers.
    internalOnly: false,
  },
  footage: {
    base:        '/api/footage-share-links',
    listPath:    id => `/api/footage-share-links/footage/${id}`,
    createBody:  id => ({ footageId: id }),
    openLabel:   '\u2934 Share Clip',
    showComments: false,
    // The API refuses external users on every footage share route; the UI hides
    // the control entirely so it never offers an action that will 403.
    internalOnly: true,
  },
};

export default function ShareLinkManager({
  videoAssetId,
  resourceType = 'video',
  resourceId,
  // Set when the resource cannot be shared right now (e.g. a cold-archived
  // clip). Existing links stay manageable; generating a new one is disabled
  // with this as the reason.
  disabledReason = null,
}) {
  const { user } = useAuth();
  const [open,          setOpen]          = useState(false);
  const [links,         setLinks]         = useState([]);
  const [generating,    setGenerating]    = useState(false);
  const [copiedId,      setCopiedId]      = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [error,         setError]         = useState(null);

  const cfg = RESOURCES[resourceType] || RESOURCES.video;
  // videoAssetId is the pre-generalisation prop name — still honoured.
  const id  = resourceId ?? videoAssetId;

  const hasRole = user?.roles?.some(r =>
    ['Admin', 'Power User', 'Editor', 'Post Lead', 'Post_Lead'].includes(r)
  );
  const canManage = hasRole && !(cfg.internalOnly && user?.isExternal);

  useEffect(() => {
    if (!open || !canManage || !id) return;
    api.get(cfg.listPath(id))
      .then(r => setLinks(r.data))
      .catch(() => {});
    // cfg is derived from resourceType, which is in the dep list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, id, canManage, resourceType]);

  if (!canManage || !id) return null;

  async function handleGenerate() {
    setGenerating(true);
    setError(null);
    try {
      const res = await api.post(cfg.base, cfg.createBody(id));
      setLinks(prev => [res.data, ...prev]);
    } catch (err) {
      // The server's reason is the useful message here — e.g. "archived to
      // Storage, restore it first" — so surface it rather than a generic string.
      setError(err?.response?.data?.error || 'Failed to generate share link.');
    } finally {
      setGenerating(false);
    }
  }

  async function handlePatch(link, patch) {
    try {
      const res = await api.patch(`${cfg.base}/${link.id}`, patch);
      setLinks(prev => prev.map(l => l.id === link.id ? { ...l, ...res.data } : l));
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to update share link.');
    }
  }

  async function handleDelete(link) {
    try {
      await api.delete(`${cfg.base}/${link.id}`);
      setLinks(prev => prev.filter(l => l.id !== link.id));
      setDeleteConfirm(null);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to delete share link.');
    }
  }

  function handleCopy(link) {
    navigator.clipboard.writeText(link.url);
    setCopiedId(link.id);
    setTimeout(() => setCopiedId(null), 2000);
  }

  return (
    <>
      {/* Toggle button — sits inline with other action buttons in VideoCard's flex row */}
      <button
        className="btn-ghost"
        onClick={() => setOpen(v => !v)}
        style={{ fontSize: 12 }}
      >
        {open ? '\u25be Share Links' : cfg.openLabel}
      </button>

      {/* Panel — flex-basis:100% forces it onto its own line inside the parent flex-wrap row */}
      {open && (
        <div style={{ flexBasis: '100%', marginTop: 4, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {links.length === 0 && (
            <div style={{ fontSize: 11, color: 'var(--tx-faint)', padding: '4px 0' }}>No share links yet.</div>
          )}
          {links.map(link => (
            <ShareLinkRow
              key={link.id}
              link={link}
              isCopied={copiedId === link.id}
              onCopy={handleCopy}
              onPatch={handlePatch}
              onDeleteClick={setDeleteConfirm}
              showComments={cfg.showComments}
            />
          ))}
          {error && (
            <div style={{ fontSize: 11, color: 'var(--s-incomplete-t)', lineHeight: 1.5 }}>{error}</div>
          )}
          <button
            className="btn-ghost"
            style={{ fontSize: 12, alignSelf: 'flex-start', opacity: disabledReason ? 0.5 : 1 }}
            onClick={handleGenerate}
            disabled={generating || !!disabledReason}
            title={disabledReason || undefined}
          >
            {generating ? 'Generating…' : '+ Generate Share Link'}
          </button>
          {disabledReason && (
            <div style={{ fontSize: 11, color: 'var(--tx-faint)', lineHeight: 1.5 }}>{disabledReason}</div>
          )}
        </div>
      )}

      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="vc-modal" style={{ width: 380 }} onClick={e => e.stopPropagation()}>
            <div className="vc-header">
              <div><div className="vc-title">Delete Share Link?</div></div>
              <button className="vc-close" onClick={() => setDeleteConfirm(null)}>✕</button>
            </div>
            <div style={{ padding: '16px 28px', fontSize: 13, color: 'var(--tx-secondary)', lineHeight: 1.6 }}>
              This permanently deletes the share link. Anyone with the URL will no longer be able to access it.
            </div>
            <div className="vc-footer">
              <div />
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn-ghost" onClick={() => setDeleteConfirm(null)}>Cancel</button>
                <button
                  onClick={() => handleDelete(deleteConfirm)}
                  style={{ background: 'rgba(212,85,85,0.15)', color: 'var(--s-incomplete-t)', border: '1px solid rgba(212,85,85,0.35)', borderRadius: 'var(--radius-sm)', padding: '9px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font)' }}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function ShareLinkRow({ link, isCopied, onCopy, onPatch, onDeleteClick, showComments = true }) {
  return (
    <div style={{
      background: 'var(--bg-card2)', borderRadius: 8, padding: '10px 12px',
      border: '1px solid var(--border)',
      opacity: link.isActive ? 1 : 0.55,
    }}>
      {/* URL + copy */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        {!link.isActive && (
          <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.05em', background: 'rgba(139,109,255,0.15)', color: 'var(--violet)', borderRadius: 3, padding: '1px 5px', flexShrink: 0 }}>
            INACTIVE
          </span>
        )}
        <span style={{ flex: 1, fontSize: 10, color: 'var(--tx-faint)', fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {link.url}
        </span>
        <button
          onClick={() => onCopy(link)}
          style={{ flexShrink: 0, background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 600, fontFamily: 'var(--font)', padding: '2px 6px', color: isCopied ? '#22c55e' : 'var(--violet)' }}
        >
          {isCopied ? '✓ Copied!' : '⎘ Copy'}
        </button>
      </div>

      {/* Stats */}
      <div style={{ fontSize: 10, color: 'var(--tx-faint)', marginBottom: 8 }}>
        {link.viewCount === 0
          ? 'Never viewed'
          : `Viewed ${link.viewCount} time${link.viewCount !== 1 ? 's' : ''}`}
        {link.lastViewedAt && ` · Last viewed ${timeAgo(link.lastViewedAt)}`}
      </div>

      {/* Toggles */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 8 }}>
        {/* Footage has no comment model, so the toggle is absent rather than
            present-and-inert. */}
        {showComments && (
          <ToggleSwitch label="Allow comments" checked={link.allowComments} onChange={v => onPatch(link, { allowComments: v })} />
        )}
        <ToggleSwitch label="Allow download" checked={link.allowDownload} onChange={v => onPatch(link, { allowDownload: v })} />
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <button
          className="btn-ghost"
          style={{ fontSize: 11, padding: '4px 10px' }}
          onClick={() => onPatch(link, { isActive: !link.isActive })}
        >
          {link.isActive ? 'Deactivate' : 'Reactivate'}
        </button>
        <button
          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: 'var(--s-incomplete-t)', fontFamily: 'var(--font)', padding: '4px 6px' }}
          onClick={() => onDeleteClick(link)}
        >
          Delete
        </button>
      </div>
    </div>
  );
}

function ToggleSwitch({ label, checked, onChange }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', userSelect: 'none', fontSize: 10, color: 'var(--tx-secondary)' }}>
      <div
        onClick={() => onChange(!checked)}
        style={{
          width: 28, height: 16, borderRadius: 8, position: 'relative',
          background: checked ? 'var(--violet)' : 'var(--bg-input)',
          border: '1px solid var(--border-mid)',
          cursor: 'pointer', transition: 'background 0.15s', flexShrink: 0,
        }}
      >
        <div style={{
          position: 'absolute', top: 2,
          left: checked ? 13 : 2,
          width: 10, height: 10, borderRadius: '50%',
          background: checked ? '#fff' : 'var(--tx-faint)',
          transition: 'left 0.15s',
        }} />
      </div>
      {label}
    </label>
  );
}
